import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/api';
import {
  isPushSupported,
  pushPermissionState,
  getCurrentSubscription,
  enablePushNotifications,
  disablePushNotifications,
} from '../lib/push';
import DobPicker from '../components/DobPicker';

export default function ProfilePage() {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [wallet, setWallet]   = useState(null);
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied]   = useState(false);
  const [dobInput, setDobInput] = useState('');
  const [dobSaving, setDobSaving] = useState(false);
  const [dobMsg, setDobMsg] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Push notifications
  const [pushSupported, setPushSupported] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushPermission, setPushPermission] = useState('default'); // default | granted | denied
  const [pushSaving, setPushSaving] = useState(false);
  const [pushMsg, setPushMsg] = useState('');

  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [marketingSaving, setMarketingSaving] = useState(false);
  const [marketingMsg, setMarketingMsg] = useState('');

  useEffect(() => {
    Promise.all([
      supabase.rpc('get_my_profile'),
      supabase.rpc('get_my_wallet_data'),
      supabase.rpc('get_my_referral_info'),
      supabase.rpc('get_my_marketing_opt_in'),
    ]).then(([p, w, r, m]) => {
      setProfile(p.data);
      setWallet(w.data);
      setReferral(r.data);
      // Prefer the dedicated read; it always reflects the stored value.
      if (m && typeof m.data === 'boolean') setMarketingOptIn(m.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // Only fall back to the profile field if it's actually present, so a
    // get_my_profile that omits marketing_opt_in doesn't reset the toggle.
    if (profile && 'marketing_opt_in' in profile) setMarketingOptIn(!!profile.marketing_opt_in);
  }, [profile]);

  useEffect(() => {
    if (profile?.date_of_birth) setDobInput(profile.date_of_birth);
  }, [profile?.date_of_birth]);

  // Determine current push status when the page loads
  useEffect(() => {
    if (!isPushSupported()) {
      setPushSupported(false);
      return;
    }
    setPushPermission(pushPermissionState());
    getCurrentSubscription().then(sub => setPushEnabled(!!sub));
  }, []);

  async function togglePush() {
    setPushSaving(true); setPushMsg('');
    if (pushEnabled) {
      await disablePushNotifications();
      setPushEnabled(false);
      setPushMsg('Notifications turned off');
    } else {
      const result = await enablePushNotifications();
      if (result.ok) {
        setPushEnabled(true);
        setPushPermission('granted');
        setPushMsg('Notifications turned on');
      } else if (result.reason === 'denied') {
        setPushPermission('denied');
        setPushMsg('Permission denied. Enable notifications for this app in your browser settings.');
      } else if (result.reason === 'unsupported') {
        setPushMsg('Push notifications aren\'t supported on this device or browser.');
      } else if (result.reason === 'not_configured') {
        setPushMsg('Push notifications are not yet available — coming soon.');
      } else {
        setPushMsg('Couldn\'t enable notifications. Please try again.');
      }
    }
    setPushSaving(false);
    setTimeout(() => setPushMsg(''), 4000);
  }

  async function saveDob() {
    if (!dobInput) return;
    setDobSaving(true); setDobMsg('');
    const { data, error } = await supabase.rpc('update_my_dob', { new_dob: dobInput });
    if (!error && data?.ok) {
      setDobMsg('✓ Saved');
      setProfile(p => ({ ...p, date_of_birth: dobInput }));
      setTimeout(() => setDobMsg(''), 2500);
    } else {
      setDobMsg(error?.message ? `✗ ${error.message}` : '✗ Error saving');
    }
    setDobSaving(false);
  }

  async function toggleMarketing() {
    const next = !marketingOptIn;
    setMarketingOptIn(next); // optimistic
    setMarketingSaving(true); setMarketingMsg('');
    // Save through a SECURITY DEFINER RPC (same pattern as update_my_dob).
    // This avoids the fragile auth.getUser() refresh that could fail and
    // silently drop the save, snapping the toggle back to off.
    const { data, error } = await supabase.rpc('update_my_marketing_opt_in', { opt_in: next });
    if (!error && data?.ok) {
      setProfile(p => p ? { ...p, marketing_opt_in: next } : p);
      setMarketingMsg(next ? 'Subscribed — we\'ll keep you posted.' : 'Unsubscribed from email updates.');
      setTimeout(() => setMarketingMsg(''), 4000);
    } else {
      setMarketingOptIn(!next); // revert on failure
      setMarketingMsg('Couldn\'t save — please try again.');
    }
    setMarketingSaving(false);
  }

  async function logout() {
    await supabase.auth.signOut();
    nav('/login');
  }

  async function handleDeleteAccount() {
    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') return;
    setDeleting(true); setDeleteError('');
    const { data, error } = await supabase.rpc('delete_my_account');
    if (error || !data?.ok) {
      setDeleteError(error?.message || 'Could not delete account. Please contact us.');
      setDeleting(false);
      return;
    }
    // Sign out and bounce to login
    try { await supabase.auth.signOut(); } catch (_) {}
    nav('/login');
  }

  function openDeleteModal() {
    setDeleteConfirmText('');
    setDeleteError('');
    setShowDeleteModal(true);
  }

  function closeDeleteModal() {
    if (deleting) return;
    setShowDeleteModal(false);
  }

  function copyReferralLink() {
    const link = `https://app.breakthroughgym.co.uk/#/signup?ref=${referral?.referral_code}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  function shareReferralLink() {
    const link = `https://app.breakthroughgym.co.uk/#/signup?ref=${referral?.referral_code}`;
    if (navigator.share) {
      navigator.share({
        title: 'Join Team Breakthrough',
        text: 'Join me at Team Breakthrough — Box · Bath · Corsham. Use my referral link to sign up:',
        url: link,
      });
    } else {
      copyReferralLink();
    }
  }

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  const initials = profile
    ? `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase()
    : '?';

  return (
    <div className="page">

      {/* Member card */}
      <div style={{ background: '#1A1A1A', borderRadius: 20, padding: 24, marginBottom: 20, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: -30, top: -30, width: 140, height: 140, borderRadius: '50%', background: '#FF6200', opacity: 0.15 }} />
        <div style={{ position: 'absolute', right: 20, bottom: -20, width: 80, height: 80, borderRadius: '50%', background: '#FF6200', opacity: 0.08 }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div style={{
            width: 60, height: 60, borderRadius: '50%', background: '#FF6200',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.4rem', fontWeight: 800, color: '#fff', flexShrink: 0,
          }}>
            {initials}
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.2rem' }}>
              {profile?.first_name} {profile?.last_name}
            </div>
            <div style={{ color: '#888', fontSize: '0.8rem', marginTop: 2 }}>{profile?.email}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#FF6200' }}>{wallet?.balance || 0}</div>
            <div style={{ fontSize: '0.7rem', color: '#888', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Points</div>
          </div>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#FF6200' }}>{referral?.successful_referrals || 0}</div>
            <div style={{ fontSize: '0.7rem', color: '#888', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Referrals</div>
          </div>
        </div>

        <div style={{ marginTop: 16, fontSize: '0.6rem', fontWeight: 700, color: '#FF6200', letterSpacing: '0.18em' }}>
          TRAIN · RECOVER · BREAKTHROUGH
        </div>
      </div>

      {/* My Progress */}
      <div className="section-title">My Progress</div>
      <button
        type="button"
        onClick={() => nav('/progress')}
        className="card"
        style={{
          width: '100%', textAlign: 'left', padding: 20, marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
          border: 'none', background: '#fff',
        }}
      >
        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--green-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1.3rem' }}>
          📈
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 2 }}>Track your progress</div>
          <p style={{ color: 'var(--grey-700)', fontSize: '0.85rem', margin: 0, lineHeight: 1.4 }}>
            Strength trends from your logged lifts, personal bests and InBody scan history.
          </p>
        </div>
        <div style={{ color: 'var(--grey-500)', fontSize: '1.4rem', lineHeight: 1 }}>›</div>
      </button>

      {/* Birthday */}
      <div className="section-title">Birthday Bonus</div>
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <div style={{ fontSize: '1.6rem', lineHeight: 1, flexShrink: 0 }}>🎂</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>
              {profile?.date_of_birth ? 'Birthday saved' : 'Earn 20 points on your birthday'}
            </div>
            <p style={{ color: 'var(--grey-700)', fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>
              {profile?.date_of_birth
                ? 'You\'ll get 20 bonus points credited to your wallet on your birthday, every year.'
                : 'Add your date of birth and we\'ll credit 20 bonus points to your wallet on your big day, every year.'}
            </p>
          </div>
        </div>
        <DobPicker value={dobInput} onChange={setDobInput} />
        <button
          type="button"
          className="btn btn-primary btn-full"
          onClick={saveDob}
          disabled={dobSaving || !dobInput || dobInput === profile?.date_of_birth}
          style={{ marginTop: 10 }}
        >
          {dobSaving ? 'Saving…' : profile?.date_of_birth ? 'Update' : 'Save'}
        </button>
        {dobMsg && <div style={{ marginTop: 8, fontSize: '0.8rem', color: dobMsg.startsWith('✓') ? 'var(--green)' : 'var(--red, #d33)' }}>{dobMsg}</div>}
      </div>

      {/* Refer a Friend */}
      <div className="section-title">Refer a Friend</div>
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 6 }}>Earn 30 points per referral</div>
        <p style={{ color: 'var(--grey-700)', fontSize: '0.85rem', marginBottom: 16, lineHeight: 1.5 }}>
          Share your unique code. When a friend signs up and gets a gym membership or buys a wellness pack, you earn 30 points.
        </p>

        <div style={{ background: 'var(--grey-100)', borderRadius: 10, padding: '14px 16px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Your Code</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 900, letterSpacing: '0.12em' }}>{referral?.referral_code || '—'}</div>
          </div>
          <button type="button" onClick={copyReferralLink} style={{
            background: copied ? 'var(--green-pale)' : 'var(--grey-300)',
            color: copied ? 'var(--green)' : 'var(--grey-700)',
            border: 'none', borderRadius: 8, padding: '8px 14px',
            fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
          }}>
            {copied ? '✓ Copied!' : 'Copy'}
          </button>
        </div>

        <button type="button" onClick={shareReferralLink} className="btn btn-primary btn-full">
          Share Referral Link
        </button>

        {referral?.successful_referrals > 0 && (
          <div style={{ marginTop: 12, textAlign: 'center', fontSize: '0.85rem', color: 'var(--grey-700)' }}>
            🎉 You've referred <strong>{referral.successful_referrals}</strong> friend{referral.successful_referrals !== 1 ? 's' : ''} — earning <strong>{referral.successful_referrals * 30} bonus points</strong>!
          </div>
        )}
      </div>

      {/* Review Rewards */}
      <div className="section-title">Leave a Review</div>
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <div style={{ fontSize: '1.6rem', lineHeight: 1, flexShrink: 0 }}>⭐</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>Earn 10 points per review</div>
            <p style={{ color: 'var(--grey-700)', fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>
              Love what we do? Help others find us. Leave a 5-star review on Google and/or Facebook — we'll credit 10 bonus points to your wallet for each one. Just show staff your review.
            </p>
          </div>
        </div>
        <a
          href="https://g.page/r/Ccm8aEsSYbAAEAE/review"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary btn-full"
          style={{ textAlign: 'center', textDecoration: 'none', display: 'block', marginBottom: 8 }}
        >
          Leave a Google Review →
        </a>
        <a
          href="https://www.facebook.com/exclusivegymbath/reviews"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-full"
          style={{
            textAlign: 'center',
            textDecoration: 'none',
            display: 'block',
            background: '#1877F2',
            color: '#fff',
            border: 'none',
          }}
        >
          Leave a Facebook Review →
        </a>
      </div>

      {/* Notifications */}
      {pushSupported && (
        <>
          <div className="section-title">Notifications</div>
          <div className="card" style={{ padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>
                  {pushEnabled ? 'Notifications on' : 'Get notifications'}
                </div>
                <p style={{ color: 'var(--grey-700)', fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>
                  {pushEnabled
                    ? 'You\'ll get reminders for upcoming bookings, points expiry warnings and bonus alerts.'
                    : 'Turn on to get booking reminders, points expiry warnings and bonus alerts.'}
                </p>
              </div>
              <button
                type="button"
                onClick={togglePush}
                disabled={pushSaving || pushPermission === 'denied'}
                style={{
                  width: 56, height: 32, borderRadius: 999, border: 'none', flexShrink: 0,
                  cursor: pushPermission === 'denied' ? 'not-allowed' : 'pointer',
                  background: pushEnabled ? '#FF6200' : 'var(--grey-300)',
                  position: 'relative', transition: 'background 0.2s',
                  opacity: pushSaving ? 0.6 : 1,
                }}
              >
                <div style={{
                  position: 'absolute', top: 3, left: pushEnabled ? 27 : 3,
                  width: 26, height: 26, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </button>
            </div>
            {pushPermission === 'denied' && (
              <div style={{ marginTop: 12, padding: 10, background: '#FFF8E1', border: '1px solid #FFE0B2', borderRadius: 8, fontSize: '0.78rem', color: '#5D4037', lineHeight: 1.5 }}>
                You've blocked notifications for this app. To turn them back on, open your browser/iOS settings → Notifications → allow.
              </div>
            )}
            {pushMsg && (
              <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--grey-700)' }}>{pushMsg}</div>
            )}
          </div>
        </>
      )}

      {/* Email preferences */}
      <div className="section-title">Email Updates</div>
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>
              Offers &amp; updates
            </div>
            <p style={{ color: 'var(--grey-700)', fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>
              {marketingOptIn
                ? 'You\'re getting occasional offers, events and wellness tips by email.'
                : 'Turn on to get occasional offers, events and wellness tips by email. We\'ll never share your details.'}
            </p>
          </div>
          <button
            type="button"
            onClick={toggleMarketing}
            disabled={marketingSaving}
            style={{
              width: 56, height: 32, borderRadius: 999, border: 'none', flexShrink: 0, cursor: 'pointer',
              background: marketingOptIn ? '#FF6200' : 'var(--grey-300)',
              position: 'relative', transition: 'background 0.2s', opacity: marketingSaving ? 0.6 : 1,
            }}
          >
            <div style={{
              position: 'absolute', top: 3, left: marketingOptIn ? 27 : 3,
              width: 26, height: 26, borderRadius: '50%', background: '#fff',
              transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </button>
        </div>
        {marketingMsg && (
          <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--grey-700)' }}>{marketingMsg}</div>
        )}
      </div>

      {/* Contact */}
      <div className="section-title">Contact Us</div>
      <div className="card" style={{ padding: 16, marginBottom: 20 }}>
        <a href="tel:01225743801" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--grey-100)', textDecoration: 'none', color: 'var(--black)' }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--green-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.04a16 16 0 006.29 6.29l1.42-1.42a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
            </svg>
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>01225 743 801</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>Call us</div>
          </div>
        </a>
        <a href="mailto:info@breakthroughgym.co.uk" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', textDecoration: 'none', color: 'var(--black)' }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--green-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>info@breakthroughgym.co.uk</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>Email us</div>
          </div>
        </a>
      </div>

      {/* Log out */}
      <button className="btn btn-secondary btn-full" onClick={logout} style={{ marginBottom: 12 }}>Log Out</button>

      {/* Delete account — quiet but accessible (Apple App Store requires this) */}
      <button
        type="button"
        onClick={openDeleteModal}
        style={{
          width: '100%',
          background: 'transparent',
          border: '1px solid #FFCDD2',
          color: '#D32F2F',
          padding: '12px',
          borderRadius: 'var(--radius-sm)',
          fontWeight: 600,
          fontSize: '0.85rem',
          cursor: 'pointer',
          marginBottom: 12,
        }}
      >
        Delete My Account
      </button>

      <div style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--grey-500)', paddingBottom: 8 }}>
        Unit 2, The Ley Business Park, Box, SN13 8EH
      </div>

      {/* Delete account confirmation modal */}
      {showDeleteModal && (
        <div
          onClick={closeDeleteModal}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20, zIndex: 1000,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 16, maxWidth: 420, width: '100%',
              padding: 24, maxHeight: '85vh', overflowY: 'auto',
            }}
          >
            <h2 style={{ fontSize: '1.2rem', fontWeight: 800, marginBottom: 12, color: '#D32F2F' }}>
              Delete your account?
            </h2>

            <p style={{ fontSize: '0.9rem', lineHeight: 1.5, color: 'var(--grey-700)', marginBottom: 14 }}>
              This is permanent and cannot be undone. By proceeding you will:
            </p>

            <ul style={{ fontSize: '0.88rem', lineHeight: 1.6, color: 'var(--grey-700)', paddingLeft: 18, marginBottom: 16 }}>
              <li>Lose your <strong>{wallet?.balance || 0} wellness points</strong></li>
              <li>Have any <strong>upcoming bookings cancelled</strong> automatically</li>
              <li>Have your profile, contact details and history permanently erased</li>
              <li>Be signed out and unable to log back in with this email</li>
            </ul>

            <div style={{ background: '#FFF8E1', border: '1px solid #FFE0B2', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: '0.82rem', lineHeight: 1.5, color: '#5D4037' }}>
              <strong>Wellness points are non-refundable.</strong> Any unused points in your wallet will be permanently lost on account deletion. If you have questions, email <a href="mailto:info@breakthroughgym.co.uk" style={{ color: '#5D4037', fontWeight: 700 }}>info@breakthroughgym.co.uk</a> before proceeding.
            </div>

            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 6, color: 'var(--grey-700)' }}>
              Type <span style={{ fontFamily: 'monospace', background: 'var(--grey-100)', padding: '2px 6px', borderRadius: 4 }}>DELETE</span> to confirm
            </label>
            <input
              className="form-input"
              type="text"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              autoCapitalize="characters"
              style={{ marginBottom: 12 }}
            />

            {deleteError && (
              <div style={{ fontSize: '0.85rem', color: '#D32F2F', marginBottom: 12 }}>
                {deleteError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={deleting}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText.trim().toUpperCase() !== 'DELETE'}
                style={{
                  flex: 1,
                  background: deleteConfirmText.trim().toUpperCase() === 'DELETE' && !deleting ? '#D32F2F' : 'var(--grey-300)',
                  color: '#fff',
                  border: 'none',
                  padding: '12px',
                  borderRadius: 'var(--radius-sm)',
                  fontWeight: 700,
                  cursor: deleteConfirmText.trim().toUpperCase() === 'DELETE' && !deleting ? 'pointer' : 'not-allowed',
                }}
              >
                {deleting ? 'Deleting…' : 'Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
